/**
 * Money formatting utilities for organisation-configured currencies
 *
 * Currency Configuration:
 * - Currency is configured per organization in `organizations.billing_settings.currency`
 * - Access via `useCurrency()` hook which fetches from billing settings
 * - DEFAULT_CURRENCY is a fallback when org settings aren't loaded yet
 *
 * Amount Storage:
 * - ALL monetary amounts are stored in minor currency units (cents/centimes)
 * - This avoids floating-point precision issues
 * - 1 CHF = 100 cents, 1 EUR = 100 cents, 1 USD = 100 cents, etc.
 */

export const DEFAULT_CURRENCY = 'CHF'; // Fallback when org settings unavailable

/**
 * Format an amount in the Swiss money style, for every currency and every UI
 * language: the code in front, apostrophe grouping, two decimals, the sign after
 * the code (`CHF 1'234.50`, `EUR -1'234.50`). `internal/money` prints invoices and
 * dunning letters the same way, so a screen and the PDF never disagree, and
 * `parseMoney` reads the output back.
 *
 * @param amount - Amount in major currency units (e.g., 100.00 for 100 EUR)
 * @param includeSymbol - Whether to prepend the currency code
 * @param currency - ISO currency code (defaults to organisation fallback)
 * @returns Formatted string like "EUR 1'234.56" or "1'234.56"
 */
export function formatMoney(
  amount: number,
  includeSymbol = true,
  currency: string = DEFAULT_CURRENCY
): string {
  const [integerPart, decimalPart] = amount.toFixed(2).split('.');
  const withSeparators = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const result = `${withSeparators}.${decimalPart}`;
  return includeSymbol ? `${currency} ${result}` : result;
}

/**
 * Format an amount stored in minor units (cents/Rappen), the unit of every API amount.
 *
 * @param cents - Amount in minor units
 * @param currency - ISO currency code from `useCurrency()` or the record; empty falls back to the org default
 * @example formatMoneyCents(123450, 'EUR') // "EUR 1'234.50"
 */
export function formatMoneyCents(cents: number, currency: string | null | undefined): string {
  return formatMoney(minorToMajor(cents), true, currency || DEFAULT_CURRENCY);
}

/**
 * @deprecated Use formatMoney instead. Alias kept for backwards compatibility.
 */
export const formatCHF = formatMoney;

/**
 * Convert minor units to major units (e.g., cents to EUR, Rappen to CHF)
 * @param minorUnits - Amount in minor units (1/100 of major unit)
 * @returns Amount in major currency units
 */
export function minorToMajor(minorUnits: number): number {
  return minorUnits / 100;
}

/**
 * @deprecated Use minorToMajor instead. Alias kept for backwards compatibility.
 */
export const rappenToCHF = minorToMajor;

/**
 * Convert major units to minor units (e.g., EUR to cents, CHF to Rappen)
 * @param majorUnits - Amount in major currency units
 * @returns Amount in minor units (rounded to nearest integer)
 */
export function majorToMinor(majorUnits: number): number {
  return Math.round(majorUnits * 100);
}

/**
 * @deprecated Use majorToMinor instead. Alias kept for backwards compatibility.
 */
export const chfToRappen = majorToMinor;

/**
 * Parse a formatted currency string back to a number
 * @param formatted - String like "EUR 1'234.56" or "1'234.56"
 * @param currency - ISO currency code (defaults to organisation fallback)
 * @returns Number value in major currency units
 */
export function parseMoney(
  formatted: string,
  currency: string = DEFAULT_CURRENCY
): number {
  // Remove currency symbol and spaces using string operations (avoids RegExp injection)
  let cleaned = formatted.trim();
  const upperCleaned = cleaned.toUpperCase();
  const upperCurrency = currency.toUpperCase();

  if (upperCleaned.startsWith(upperCurrency)) {
    cleaned = cleaned.slice(currency.length).trimStart();
  }

  // Remove thousand separators (apostrophes)
  const withoutSeparators = cleaned.replace(/'/g, '');

  // Parse as float
  const parsed = parseFloat(withoutSeparators);

  return isNaN(parsed) ? 0 : parsed;
}

/**
 * @deprecated Use parseMoney instead. Alias kept for backwards compatibility.
 */
export const parseCHF = parseMoney;

/**
 * Format a value for display in an input field (without currency symbol)
 * @param value - Value in major currency units
 * @param currency - ISO currency code (defaults to organisation fallback)
 * @returns Formatted string with thousand separators
 */
export function formatMoneyInput(
  value: number | string,
  currency: string = DEFAULT_CURRENCY
): string {
  const numValue =
    typeof value === 'string' ? parseMoney(value, currency) : value;
  return formatMoney(numValue, false, currency);
}

/**
 * @deprecated Use formatMoneyInput instead. Alias kept for backwards compatibility.
 */
export const formatCHFInput = formatMoneyInput;

/**
 * Validate that an array of percentages sums to exactly 100
 * @param percentages - Array of percentage values
 * @param tolerance - Tolerance for floating point comparison (default 0.01)
 * @returns true if sum equals 100 within tolerance
 */
export function validatePercentageSum(percentages: number[], tolerance = 0.01): boolean {
  const sum = percentages.reduce((acc, val) => acc + val, 0);
  return Math.abs(sum - 100) < tolerance;
}

/**
 * Format a percentage value for display
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places (default 0)
 * @returns Formatted string like "50%" or "50.5%"
 * @deprecated Ignores the locale; use `formatPercent(value / 100, locale, decimals)` from `@/lib/format`.
 */
export function formatPercentage(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Parse a percentage string to a number
 * @param formatted - String like "50%" or "50.5%"
 * @returns Number value (0-100)
 */
export function parsePercentage(formatted: string): number {
  const cleaned = formatted.replace(/%\s*$/, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Clamp a value between min and max
 * @param value - Value to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
